import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Building2, FileText, Zap, Clock, Trophy, Award, BarChart3, ClipboardList } from "lucide-react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";

// Format number to k notation (e.g., 9501 -> 9.5k)
const formatNumber = (num: number | string): string => {
  const value = typeof num === 'string' ? parseInt(num) : num;
  
  if (value >= 1000000) {
    return (value / 1000000).toFixed(1) + 'M';
  } else if (value >= 1000) {
    return (value / 1000).toFixed(1) + 'k';
  }
  return value.toString();
};

// Format position with ordinal suffix (1st, 2nd, 3rd, etc.)
const formatPosition = (position: number): string => {
  if (!position || position === 0) return 'N/A';
  
  const lastDigit = position % 10;
  const lastTwoDigits = position % 100;
  
  let suffix = 'th';
  if (lastTwoDigits < 11 || lastTwoDigits > 13) {
    if (lastDigit === 1) suffix = 'st';
    else if (lastDigit === 2) suffix = 'nd';
    else if (lastDigit === 3) suffix = 'rd';
  }
  
  return `${position}${suffix}`;
};

export default function StatsCards() {
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  const { data: stats } = useQuery<any>({
    queryKey: ["/api/dashboard/stats"],
    staleTime: 0, // Mark data as stale immediately
    refetchOnMount: true, // Refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });

  const handleLeaderboardClick = () => {
    navigate('/leaderboard');
  };

  const handleUsageClick = () => {
    navigate('/admin/usage');
  };

  const cards = [
    {
      title: "VBRs You Created",
      value: stats?.userVbrsGenerated || 0,
      icon: Award,
      bgColor: "bg-gradient-to-br from-purple-400 to-purple-500",
      iconBg: "bg-white/20",
      testId: "stat-user-vbrs",
      onClick: null,
      adminOnly: false
    },
    {
      title: "Copy Messages You Created",
      value: stats?.copyMessagesCreated || 0,
      icon: FileText,
      bgColor: "bg-gradient-to-br from-sky-400 to-sky-500",
      iconBg: "bg-white/20",
      testId: "stat-copy-messages",
      onClick: null,
      adminOnly: false
    },
    {
      title: "CNAs You Created",
      value: stats?.userCnasCreated || 0,
      icon: ClipboardList,
      bgColor: "bg-gradient-to-br from-teal-400 to-teal-500",
      iconBg: "bg-white/20",
      testId: "stat-cnas-created",
      onClick: null,
      adminOnly: false
    },
    {
      title: "Hours You've Saved",
      value: stats?.userHoursSaved || 0,
      icon: Clock,
      bgColor: "bg-gradient-to-br from-emerald-400 to-emerald-500",
      iconBg: "bg-white/20",
      testId: "stat-user-hours-saved",
      onClick: null,
      adminOnly: false
    },
    {
      title: "Leaderboard Position",
      value: stats?.leaderboardExempt ? 'Exempt' : formatPosition(stats?.leaderboardPosition || 0),
      icon: Trophy,
      bgColor: "bg-gradient-to-br from-amber-400 to-amber-500",
      iconBg: "bg-white/20",
      testId: "stat-leaderboard-position",
      onClick: handleLeaderboardClick,
      adminOnly: false
    },
    {
      title: "Track App Usage",
      value: "View Analytics",
      icon: BarChart3,
      bgColor: "bg-gradient-to-br from-rose-400 to-rose-500",
      iconBg: "bg-white/20",
      testId: "stat-track-usage",
      onClick: handleUsageClick,
      adminOnly: true
    },
  ];

  // Filter cards based on admin status
  const visibleCards = cards.filter(card => !card.adminOnly || isAdmin);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
      {visibleCards.map((card) => {
        const Icon = card.icon;
        return (
          <Card 
            key={card.title} 
            className={`${card.bgColor} border-0 text-white hover:shadow-lg transition-all transform hover:scale-105 h-[120px] ${card.onClick ? 'cursor-pointer' : ''}`}
            onClick={card.onClick ?? undefined}
          >
            <CardContent className="p-6 h-full">
              <div className="flex items-center justify-between h-full">
                <div className="flex flex-col justify-between h-full min-h-[72px]">
                  <p className="text-white/90 text-sm font-medium leading-tight">{card.title}</p>
                  <p className="text-3xl font-bold text-white leading-none" data-testid={card.testId}>
                    {card.value}
                  </p>
                </div>
                <div className={`${card.iconBg} p-3 rounded-lg backdrop-blur-sm`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}